import { X, Check, Bell, ChevronDown, ChevronUp, User, Mail, Phone } from 'lucide-react';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Button } from '../ui/Button';
import { useNotificationsQuery, useMarkNotificationRead, useMarkAllNotificationsRead, useApproveTrialSubscription, useRejectTrialSubscription, useAcceptSubscriptionInvitation, useRejectSubscriptionInvitation } from '../../hooks/useNotifications';
import { supabase } from '../../lib/supabase';

function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  
  return date.toLocaleDateString();
}

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Notification Center Component
 * 
 * Displays a modal/dropdown with all user notifications
 * Similar to mobile notification center
 */
export function NotificationCenter({ isOpen, onClose }: NotificationCenterProps) {
  const navigate = useNavigate();
  const { data: notifications = [], isLoading } = useNotificationsQuery();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const approveTrial = useApproveTrialSubscription();
  const rejectTrial = useRejectTrialSubscription();
  const acceptInvitation = useAcceptSubscriptionInvitation();
  const rejectInvitation = useRejectSubscriptionInvitation();
  
  // Track expanded notifications and rejection reasons
  const [expandedNotifications, setExpandedNotifications] = useState<Set<string>>(new Set());
  const [rejectionReasons, setRejectionReasons] = useState<Record<string, string>>({});
  // Track approved notification IDs — triggers re-render when updated
  const [approvedNotifications, setApprovedNotifications] = useState<Set<string>>(new Set());
  // Snapshot approved notification objects so they survive query refetch/removal
  const approvedSnapshotsRef = useRef<Map<string, typeof notifications[0]>>(new Map());
  // Track which trial notifications are in "decline mode" (showing rejection reason textarea)
  const [decliningNotifications, setDecliningNotifications] = useState<Set<string>>(new Set());
  // Filter: hide feed notifications (post_comment, post_reaction) by default since they're not actionable on web
  const [showFeedNotifications, setShowFeedNotifications] = useState(false);
  // Track user profile data for subscription enrollment notifications
  const [userProfileIds, setUserProfileIds] = useState<Set<string>>(new Set());

  // Helper: Check if notification is a feed-related notification (not actionable on web)
  const isFeedNotification = (notification: typeof notifications[0]) => {
    const normalizedType = notification.type?.toLowerCase() || '';
    return normalizedType === 'post_comment' || normalizedType === 'post_reaction';
  };

  // Count unread notifications - explicitly check for false or null
  // This ensures we count all truly unread notifications (including feed notifications)
  const unreadNotifications = notifications.filter((n) => {
    // Explicitly check: is_read must be false or null/undefined to be unread
    // Using === false ensures we don't count true as unread
    // Using == null handles both null and undefined
    return n.is_read === false || n.is_read == null;
  });
  const unreadCount = unreadNotifications.length;

  // Filter notifications based on showFeedNotifications setting
  // By default, hide feed notifications since they're not actionable on web
  const filteredNotifications = showFeedNotifications
    ? notifications
    : notifications.filter((n) => !isFeedNotification(n));

  // Count feed notifications for display in filter toggle
  const feedNotificationCount = notifications.filter((n) => isFeedNotification(n)).length;

  // Merge approved snapshots back into the list so they survive query refetch
  const mergedNotifications = useMemo(() => {
    const ids = new Set(filteredNotifications.map((n) => n.id));
    const merged = [...filteredNotifications];
    for (const [id, snapshot] of approvedSnapshotsRef.current) {
      if (!ids.has(id) && approvedNotifications.has(id)) {
        merged.push({ ...snapshot, is_read: true });
      }
    }
    return merged;
  }, [filteredNotifications, approvedNotifications]);

  // Sort notifications: unread first, then by created_at DESC within each group
  const sortedNotifications = [...mergedNotifications].sort((a, b) => {
    const aIsUnread = a.is_read === false || a.is_read == null;
    const bIsUnread = b.is_read === false || b.is_read == null;

    // If one is unread and the other isn't, unread comes first
    if (aIsUnread && !bIsUnread) return -1;
    if (!aIsUnread && bIsUnread) return 1;

    // Both are in the same group (both unread or both read), sort by date DESC
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  // Collect user IDs from subscription enrollment notifications for batch fetching
  // Use useMemo to create stable Set reference and prevent infinite loops
  const userIdsFromNotifications = useMemo(() => {
    const userIds = new Set<string>();
    notifications.forEach(notification => {
      const normalizedType = notification.type?.toLowerCase() || '';
      if (normalizedType === 'subscription_enrollment' && notification.data?.user_id) {
        userIds.add(notification.data.user_id as string);
      }
    });
    // Convert to sorted array for stable comparison
    return Array.from(userIds).sort();
  }, [notifications]);

  // Track previous IDs to avoid unnecessary updates
  const prevIdsRef = useRef<string[]>([]);

  // Only update state if the user IDs actually changed
  useEffect(() => {
    const newIds = userIdsFromNotifications;
    const prevIds = prevIdsRef.current;
    
    // Compare arrays to avoid unnecessary updates
    if (newIds.length !== prevIds.length || 
        newIds.some((id, index) => id !== prevIds[index])) {
      prevIdsRef.current = newIds;
      setUserProfileIds(new Set(newIds));
    }
  }, [userIdsFromNotifications]); // Only depend on the memoized array, not the Set state

  // Fetch user profiles for subscription enrollment notifications
  const { data: userProfiles = [] } = useQuery({
    queryKey: ['user-profiles', Array.from(userProfileIds)],
    enabled: userProfileIds.size > 0,
    queryFn: async () => {
      if (userProfileIds.size === 0) return [];
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, email, phone, avatar_url')
        .in('id', Array.from(userProfileIds));
      if (error) throw error;
      return data || [];
    },
    staleTime: 60_000, // 1 minute
  });

  // Create a map of user_id -> user profile for quick lookup
  const userProfileMap = new Map(userProfiles.map(user => [user.id, user]));

  // Clean up approved snapshots and transient state when the panel closes
  useEffect(() => {
    if (!isOpen) {
      approvedSnapshotsRef.current.clear();
      setApprovedNotifications(new Set());
      setDecliningNotifications(new Set());
      setRejectionReasons({});
      setExpandedNotifications(new Set());
    }
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);
  
  // Debug: Log notification data when modal opens to diagnose count issues
  // Debug logging removed for production
  // useEffect(() => {
  //   if (isOpen && notifications.length > 0) {
  //     const unread = notifications.filter(n => n.is_read === false || n.is_read == null);
  //     const read = notifications.filter(n => n.is_read === true);
  //     console.log('[NotificationCenter] Notification Breakdown:', {
  //       total: notifications.length,
  //       unreadCount: unread.length,
  //       readCount: read.length,
  //       unreadNotifications: unread.map(n => ({
  //         id: n.id.substring(0, 8),
  //         title: n.title,
  //         is_read: n.is_read,
  //       })),
  //       readNotifications: read.slice(0, 3).map(n => ({
  //         id: n.id.substring(0, 8),
  //         title: n.title,
  //         is_read: n.is_read,
  //       })),
  //       note: 'Count badge shows UNREAD notifications only, not total visible',
  //     });
  //   }
  // }, [isOpen, notifications, unreadCount]);

  if (!isOpen) return null;

  const handleMarkAllRead = () => {
    if (unreadCount > 0) {
      markAllRead.mutate();
    }
  };

  const handleNotificationClick = (notification: typeof notifications[0]) => {
    const normalizedType = notification.type?.toLowerCase() || '';
    const data = notification.data || {};
    
    // For trial subscription requests, toggle expand instead of routing
    if (notification.type === 'TRIAL_SUBSCRIPTION_REQUEST') {
      const isExpanded = expandedNotifications.has(notification.id);
      if (isExpanded) {
        setExpandedNotifications(prev => {
          const next = new Set(prev);
          next.delete(notification.id);
          return next;
        });
      } else {
        setExpandedNotifications(prev => new Set(prev).add(notification.id));
      }
      return;
    }
    
    // For subscription enrollment notifications, toggle expand to show user bio
    if (normalizedType === 'subscription_enrollment' && data.user_id) {
      const isExpanded = expandedNotifications.has(notification.id);
      if (isExpanded) {
        setExpandedNotifications(prev => {
          const next = new Set(prev);
          next.delete(notification.id);
          return next;
        });
      } else {
        setExpandedNotifications(prev => new Set(prev).add(notification.id));
        // Mark as read when expanding
        if (!notification.is_read) {
          markRead.mutate(notification.id);
        }
      }
      return;
    }
    
    // For subscription invitation notifications, toggle expand to show accept/reject buttons
    if (normalizedType === 'subscription_invitation') {
      const invitationId = data.subscription_invitation_id as string | undefined;
      const enrollmentId = data.enrollment_id || data.subscription_enrollment_id;
      
      // If it has enrollment_id and user_id, this is a notification about an enrollment (not an invitation)
      // Navigate to user profile
      if (enrollmentId && data.user_id) {
        navigate(`/students?userId=${data.user_id}`);
        onClose();
        return;
      }
      
      // If it has an invitation_id, this is an actual invitation - expand to show accept/reject
      if (invitationId) {
        const isExpanded = expandedNotifications.has(notification.id);
        if (isExpanded) {
          setExpandedNotifications(prev => {
            const next = new Set(prev);
            next.delete(notification.id);
            return next;
          });
        } else {
          setExpandedNotifications(prev => new Set(prev).add(notification.id));
        }
        return;
      }
      
      // Fallback: navigate to subscriptions page if no invitation_id
      navigate('/subscriptions-plans/gym');
      onClose();
      return;
    }
    
    // Mark as read
    if (!notification.is_read) {
      markRead.mutate(notification.id);
    }
    
    // Trial subscription request - already handled above (expands)
    // But if user_id is present, we could navigate after action
    if (normalizedType === 'trial_subscription_request' && data.user_id) {
      // Don't navigate here - let the expand/collapse handle it
      // User can navigate after approve/reject if needed
      return;
    }
    
    // Age exception request - navigate to user profile if user_id available
    if (normalizedType === 'age_exception_request' && data.user_id) {
      navigate(`/students?userId=${data.user_id}`);
      onClose();
      return;
    }
    
    // Waiver required - navigate to waivers page
    if (normalizedType === 'waiver_required') {
      navigate('/damage-waivers');
      onClose();
      return;
    }
    
    // Payment method required - navigate to billing settings
    if (normalizedType === 'payment_method_required') {
      navigate('/billing/settings');
      onClose();
      return;
    }
    
    // Billing setup complete - navigate to billing page
    if (normalizedType === 'billing_setup_complete') {
      navigate('/billing');
      onClose();
      return;
    }
    
    // Trial expiring - navigate to subscriptions
    if (normalizedType === 'trial_expiring') {
      navigate('/subscriptions-plans/gym');
      onClose();
      return;
    }
    
    // Account note - navigate to user profile if user_id available
    if (normalizedType === 'account_note' && data.user_id) {
      navigate(`/students?userId=${data.user_id}`);
      onClose();
      return;
    }
    
    // New message - navigate to messaging (if we had a messaging page)
    // For now, just mark as read
    if (normalizedType === 'new_message' && data.conversation_id) {
      // TODO: Navigate to messaging when implemented
      // navigate(`/messaging/${data.conversation_id}`);
      onClose();
      return;
    }
    
    // Post comment/reaction - navigate to feed with post_id
    if ((normalizedType === 'post_comment' || normalizedType === 'post_reaction') && (data.post_id || data.postId)) {
      const postId = data.post_id || data.postId;
      navigate(`/feed?post_id=${postId}`);
      onClose();
      return;
    }
    
    // Class reminder/cancelled/rescheduled - navigate to schedule (if we had a schedule page)
    // For now, just mark as read
    if (normalizedType === 'class_reminder' || normalizedType === 'class_cancelled' || normalizedType === 'class_rescheduled') {
      // TODO: Navigate to schedule when implemented
      // navigate('/schedule');
      onClose();
      return;
    }
    
    // Gym announcement - navigate to dashboard
    if (normalizedType === 'gym_announcement') {
      navigate('/dashboard');
      onClose();
      return;
    }
    
    // System announcement - navigate to dashboard
    if (normalizedType === 'system_announcement') {
      navigate('/dashboard');
      onClose();
      return;
    }
    
    // Payment confirmation - navigate to billing
    if (normalizedType === 'payment_confirmation' || normalizedType === 'payment_received') {
      navigate('/billing');
      onClose();
      return;
    }
    
    // Payment due - navigate to billing
    if (normalizedType === 'payment_due') {
      navigate('/billing');
      onClose();
      return;
    }
    
    // Default: just close the modal
    onClose();
  };

  const handleApproveTrial = async (notification: typeof notifications[0]) => {
    const enrollmentId = notification.data?.enrollment_id;
    if (!enrollmentId || typeof enrollmentId !== 'string') {
      alert('Error: Enrollment ID not found.');
      return;
    }

    try {
      await approveTrial.mutateAsync({ enrollmentId: enrollmentId as string });
      if (!notification.is_read) {
        markRead.mutate(notification.id);
      }

      // Snapshot the notification object so it survives query refetch
      approvedSnapshotsRef.current.set(notification.id, notification);
      // Show "Approved" state — stays visible until the notification panel is closed
      setApprovedNotifications(prev => new Set(prev).add(notification.id));
      // Exit decline mode if it was open
      setDecliningNotifications(prev => {
        const next = new Set(prev);
        next.delete(notification.id);
        return next;
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to approve trial subscription.';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleRejectTrial = async (notification: typeof notifications[0]) => {
    const enrollmentId = notification.data?.enrollment_id;
    if (!enrollmentId || typeof enrollmentId !== 'string') {
      // Only show alert for actual errors
      alert('Error: Enrollment ID not found.');
      return;
    }

    const reason = rejectionReasons[notification.id]?.trim();
    if (!reason) {
      // Only show alert for validation errors
      alert('Please provide a reason for declining the trial request.');
      return;
    }

    try {
      await rejectTrial.mutateAsync({ enrollmentId: enrollmentId as string, reason });
      if (!notification.is_read) {
        markRead.mutate(notification.id);
      }
      // Collapse the notification and clean up state
      setExpandedNotifications(prev => {
        const next = new Set(prev);
        next.delete(notification.id);
        return next;
      });
      setDecliningNotifications(prev => {
        const next = new Set(prev);
        next.delete(notification.id);
        return next;
      });
      setRejectionReasons(prev => {
        const next = { ...prev };
        delete next[notification.id];
        return next;
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to decline trial subscription.';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleAcceptInvitation = async (notification: typeof notifications[0]) => {
    const invitationId = notification.data?.subscription_invitation_id;
    if (!invitationId || typeof invitationId !== 'string') {
      alert('Error: Invitation ID not found.');
      return;
    }

    try {
      const result = await acceptInvitation.mutateAsync({ invitationId: invitationId as string });
      if (result?.success !== false) {
        // Show "Accepted" state temporarily
        setApprovedNotifications(prev => new Set(prev).add(notification.id));
        if (!notification.is_read) {
          markRead.mutate(notification.id);
        }
        
        // Wait a moment to show the "Accepted" state, then close
        setTimeout(() => {
          setExpandedNotifications(prev => {
            const next = new Set(prev);
            next.delete(notification.id);
            return next;
          });
          setApprovedNotifications(prev => {
            const next = new Set(prev);
            next.delete(notification.id);
            return next;
          });
          onClose();
        }, 1000);
      } else {
        throw new Error(result?.error || 'Failed to accept invitation');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to accept subscription invitation.';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleRejectInvitation = async (notification: typeof notifications[0]) => {
    const invitationId = notification.data?.subscription_invitation_id;
    if (!invitationId || typeof invitationId !== 'string') {
      alert('Error: Invitation ID not found.');
      return;
    }

    try {
      await rejectInvitation.mutateAsync({ invitationId: invitationId as string });
      if (!notification.is_read) {
        markRead.mutate(notification.id);
      }
      setExpandedNotifications(prev => {
        const next = new Set(prev);
        next.delete(notification.id);
        return next;
      });
      onClose();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reject subscription invitation.';
      alert(`Error: ${errorMessage}`);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      aria-hidden="true"
    >
      {/* Notification Center Modal */}
      <div 
        className="bg-[#221F1F] border border-[#504A4A] rounded-lg shadow-xl w-full max-w-2xl max-h-[calc(100vh-4rem)] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#504A4A] flex-shrink-0">
          <div className="flex items-center gap-3">
            <Bell className="w-6 h-6 text-rollops-accent" />
            <h2 className="text-xl font-semibold text-text-primary">Notifications</h2>
            {unreadCount > 0 && (
              <span className="px-2.5 py-1 text-xs font-bold text-white bg-[#9B1421] rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#363232] transition-colors text-sm text-text-primary"
                aria-label="Mark all as read"
                title="Mark all non-persistent notifications as read"
              >
                <Check className="w-4 h-4" />
                <span>Mark all read</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-[#363232] transition-colors"
              aria-label="Close notifications"
            >
              <X className="w-5 h-5 text-text-primary" />
            </button>
          </div>
        </div>

        {/* Filter Toggle for Feed Notifications */}
        {feedNotificationCount > 0 && (
          <div className="px-6 py-3 border-b border-[#504A4A] bg-[#2A2525] flex-shrink-0">
            <button
              onClick={() => setShowFeedNotifications(!showFeedNotifications)}
              className="flex items-center gap-2 text-sm text-text-primary hover:text-text-secondary transition-colors"
            >
              {showFeedNotifications ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronUp className="w-4 h-4" />
              )}
              <span>
                {showFeedNotifications ? 'Hide' : 'Show'} feed notifications ({feedNotificationCount})
              </span>
            </button>
          </div>
        )}

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B1421] mx-auto" />
              <p className="mt-4 text-sm text-text-secondary">Loading notifications...</p>
            </div>
          ) : sortedNotifications.length === 0 ? (
            <div className="p-12 text-center">
              <Bell className="w-16 h-16 text-text-tertiary mx-auto mb-4" />
              <p className="text-text-secondary text-lg">
                {feedNotificationCount > 0 && !showFeedNotifications
                  ? 'No actionable notifications'
                  : 'No notifications'}
              </p>
              <p className="text-text-tertiary text-sm mt-2">
                {feedNotificationCount > 0 && !showFeedNotifications
                  ? `You have ${feedNotificationCount} feed notification${feedNotificationCount > 1 ? 's' : ''}`
                  : "You're all caught up!"}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-[#504A4A]">
              {sortedNotifications.map((notification) => {
                const normalizedType = notification.type?.toLowerCase() || '';
                const isTrialRequest = notification.type === 'TRIAL_SUBSCRIPTION_REQUEST';
                const isSubscriptionEnrollment = normalizedType === 'subscription_enrollment';
                const isSubscriptionInvitation = normalizedType === 'subscription_invitation' && 
                  !!notification.data?.subscription_invitation_id;
                const isExpanded = expandedNotifications.has(notification.id);
                const isActionable = isTrialRequest || isSubscriptionEnrollment || isSubscriptionInvitation;
                const isUnread = notification.is_read === false || notification.is_read == null;
                const userProfile = isSubscriptionEnrollment && notification.data?.user_id 
                  ? userProfileMap.get(notification.data.user_id as string)
                  : null;

                return (
                  <div
                    key={notification.id}
                    className={`w-full ${
                      isUnread ? 'bg-[#363232]/30' : ''
                    }`}
                  >
                    <div className="flex items-start gap-2 p-5 hover:bg-[#363232] transition-colors group">
                      <button
                        onClick={() => handleNotificationClick(notification)}
                        className="flex-1 text-left min-w-0"
                      >
                        <div className="flex items-start gap-4">
                          {isUnread && (
                            <div className="w-2.5 h-2.5 bg-[#9B1421] rounded-full mt-2 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <p className={`font-semibold text-base group-hover:text-text-primary ${isUnread ? 'text-text-primary' : 'text-text-secondary'}`}>
                                {notification.title}
                              </p>
                              {isActionable && (
                                <div className="flex-shrink-0">
                                  {isExpanded ? (
                                    <ChevronUp className="w-5 h-5 text-text-secondary group-hover:text-text-primary" />
                                  ) : (
                                    <ChevronDown className="w-5 h-5 text-text-secondary group-hover:text-text-primary" />
                                  )}
                                </div>
                              )}
                            </div>
                            <p className="text-sm text-text-secondary group-hover:text-text-primary mt-1.5 leading-relaxed">{notification.body}</p>
                            <p className="text-xs text-text-tertiary group-hover:text-text-secondary mt-2.5">
                              {formatTimeAgo(new Date(notification.created_at))}
                            </p>
                          </div>
                        </div>
                      </button>
                      {/* Quick mark-as-read button */}
                      {isUnread && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            markRead.mutate(notification.id);
                          }}
                          className="p-2 rounded-lg hover:bg-[#504A4A] transition-colors flex-shrink-0"
                          aria-label="Mark as read"
                          title="Mark as read"
                        >
                          <Bell className="w-4 h-4 text-text-secondary group-hover:text-text-primary" />
                        </button>
                      )}
                    </div>

                    {/* Expandable user bio for subscription enrollment notifications */}
                    {isExpanded && isSubscriptionEnrollment && userProfile && (
                      <div className="px-5 pb-5 border-t border-[#504A4A] pt-4 space-y-4">
                        <div className="bg-[#363232] rounded-lg p-4 space-y-3">
                          <div className="flex items-center gap-3">
                            {userProfile.avatar_url ? (
                              <img 
                                src={userProfile.avatar_url} 
                                alt={`${userProfile.first_name || ''} ${userProfile.last_name || ''}`}
                                className="w-12 h-12 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-[#504A4A] flex items-center justify-center">
                                <User className="w-6 h-6 text-text-secondary" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-text-primary">
                                {userProfile.first_name || ''} {userProfile.last_name || ''}
                              </h3>
                              {userProfile.email && (
                                <div className="flex items-center gap-2 mt-1">
                                  <Mail className="w-3 h-3 text-text-tertiary" />
                                  <span className="text-sm text-text-secondary truncate">{userProfile.email}</span>
                                </div>
                              )}
                              {userProfile.phone && (
                                <div className="flex items-center gap-2 mt-1">
                                  <Phone className="w-3 h-3 text-text-tertiary" />
                                  <span className="text-sm text-text-secondary">{userProfile.phone}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/students?userId=${notification.data?.user_id}`);
                              onClose();
                            }}
                            className="flex-1 bg-[#9B1421] hover:bg-[#7A0F1A] text-white"
                          >
                            View Profile
                          </Button>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!notification.is_read) {
                                markRead.mutate(notification.id);
                              }
                              setExpandedNotifications(prev => {
                                const next = new Set(prev);
                                next.delete(notification.id);
                                return next;
                              });
                            }}
                            className="flex-1 bg-[#363232] hover:bg-[#504A4A] text-text-primary border border-[#504A4A]"
                          >
                            Mark as Read
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Expandable action UI for subscription invitations */}
                    {isExpanded && isSubscriptionInvitation && (
                      <div className="px-5 pb-5 border-t border-[#504A4A] pt-4 space-y-4">
                        <div className="bg-[#363232] rounded-lg p-4">
                          <p className="text-sm text-text-secondary">
                            You&apos;ve been invited to a subscription. Accept to enroll or reject to decline the invitation.
                          </p>
                        </div>
                        <div className="flex gap-3">
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRejectInvitation(notification);
                            }}
                            disabled={acceptInvitation.isPending || rejectInvitation.isPending || approvedNotifications.has(notification.id)}
                            className="flex-1 bg-[#363232] hover:bg-[#504A4A] text-text-primary border border-[#504A4A] disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {rejectInvitation.isPending ? 'Rejecting...' : 'Reject'}
                          </Button>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAcceptInvitation(notification);
                            }}
                            disabled={acceptInvitation.isPending || rejectInvitation.isPending || approvedNotifications.has(notification.id)}
                            className={`flex-1 text-white disabled:opacity-50 disabled:cursor-not-allowed ${
                              approvedNotifications.has(notification.id)
                                ? 'bg-[#34C759] cursor-default'
                                : 'bg-[#34C759] hover:bg-[#2DB04A]'
                            }`}
                          >
                            {approvedNotifications.has(notification.id)
                              ? 'Accepted ✓'
                              : acceptInvitation.isPending
                                ? 'Accepting...'
                                : 'Accept'}
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Expandable action UI for trial subscription requests */}
                    {isExpanded && isTrialRequest && (
                      <div className="px-5 pb-5 border-t border-[#504A4A] pt-4 space-y-4">
                        {approvedNotifications.has(notification.id) ? (
                          /* ── Approved state: single "Approved - View Profile" button ── */
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              const userId = notification.data?.user_id;
                              if (userId) {
                                navigate(`/students?userId=${userId}`);
                              }
                              onClose();
                            }}
                            className="w-full bg-[#34C759] hover:bg-[#2DB04A] text-white"
                          >
                            Approved — View Profile
                          </Button>
                        ) : decliningNotifications.has(notification.id) ? (
                          /* ── Decline mode: show rejection reason textarea + confirm/cancel ── */
                          <>
                            <div>
                              <label className="block text-sm font-medium text-text-primary mb-2">
                                Reason for declining:
                              </label>
                              <textarea
                                value={rejectionReasons[notification.id] || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value.length <= 500) {
                                    setRejectionReasons(prev => ({
                                      ...prev,
                                      [notification.id]: value,
                                    }));
                                  }
                                }}
                                placeholder="Provide a reason for declining..."
                                className="w-full px-3 py-2 rounded-md text-sm bg-[#363232] text-text-primary placeholder:text-text-tertiary border border-[#504A4A] focus:outline-none focus:ring-2 focus:ring-[#9B1421]/50 resize-none"
                                rows={3}
                                maxLength={500}
                                disabled={rejectTrial.isPending}
                              />
                              <p className="text-xs text-text-secondary mt-1">
                                {rejectionReasons[notification.id]?.length || 0} / 500 characters
                              </p>
                            </div>
                            <div className="flex gap-3">
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDecliningNotifications(prev => {
                                    const next = new Set(prev);
                                    next.delete(notification.id);
                                    return next;
                                  });
                                }}
                                disabled={rejectTrial.isPending}
                                className="flex-1 bg-[#363232] hover:bg-[#504A4A] text-text-primary border border-[#504A4A] disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Cancel
                              </Button>
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRejectTrial(notification);
                                }}
                                disabled={
                                  !rejectionReasons[notification.id]?.trim() ||
                                  rejectTrial.isPending
                                }
                                className="flex-1 bg-[#9B1421] hover:bg-[#7A0F1A] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {rejectTrial.isPending ? 'Declining...' : 'Confirm Decline'}
                              </Button>
                            </div>
                          </>
                        ) : (
                          /* ── Default state: Decline + Approve buttons ── */
                          <div className="flex gap-3">
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDecliningNotifications(prev => new Set(prev).add(notification.id));
                              }}
                              disabled={approveTrial.isPending}
                              className="flex-1 bg-[#363232] hover:bg-[#504A4A] text-text-primary border border-[#504A4A] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Decline
                            </Button>
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApproveTrial(notification);
                              }}
                              disabled={approveTrial.isPending}
                              className="flex-1 bg-[#34C759] hover:bg-[#2DB04A] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {approveTrial.isPending ? 'Approving...' : 'Approve'}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
